import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";

export const AgentUpdates = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/agents/versions").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    if (!window.confirm(`Mettre à jour l'agent vers la version ${d.latest} sur ${ready} poste(s) en ligne ? L'agent redémarre tout seul (moins d'une minute de coupure).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/agents/update", {});
      if (data.sent.length) toast.success(`Mise à jour envoyée à ${data.sent.length} poste(s). Ils redémarrent l'agent tout seuls dans la minute.`);
      if (data.skipped.length) {
        const off = data.skipped.filter((s) => s.reason === "hors ligne").length;
        const other = data.skipped.length - off;
        toast.warning([off && `${off} poste(s) hors ligne`, other && `${other} poste(s) Mac/Linux à réinstaller`].filter(Boolean).join(" · ") + " : non mis à jour");
      }
      if (!data.sent.length && !data.skipped.length) toast.info("Tous les agents sont à jour");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!d) return null;
  const ready = d.published_ok ? d.outdated.filter((r) => r.auto && r.online).length : 0;
  return (
    <div className="rounded-lg border border-slate-200 p-5 mb-6" data-testid="agent-updates">
      <div className="flex flex-wrap items-start gap-4">
        <div className="mr-auto">
          <p className="text-xs uppercase tracking-wider text-slate-500">Mise à jour des agents</p>
          <p className="text-sm mt-1">Dernière version : <strong data-testid="agent-latest-version">{d.latest}</strong> · {d.outdated.length ? `${d.outdated.length} poste(s) à mettre à jour` : "tous vos postes sont à jour"}</p>
          <p className={`text-xs mt-1 ${d.published_ok ? "text-emerald-700" : "text-red-600 font-medium"}`} data-testid="agent-published-version">
            {d.published_ok
              ? `Installateur compilé à jour (version ${d.published}${d.built_at ? `, le ${fmtDate(d.built_at)}` : ""}).`
              : `Attention : l'installateur disponible n'est pas à jour (${d.published ? `version ${d.published}` : "version inconnue, ancienne compilation"}${d.built_at ? ` du ${fmtDate(d.built_at)}` : ""}). Relancez la compilation sur GitHub (onglet Actions → « Run workflow » sur la branche main).`}
          </p>
          <p className="text-xs text-slate-500 mt-1">Postes Windows : mise à jour à distance, sans réinstaller. Mac et Linux : réinstallez l'agent.</p>
        </div>
        <Button onClick={run} disabled={busy || !ready} data-testid="agent-update-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Mettre à jour les agents ({ready})
        </Button>
      </div>
      {d.outdated.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100 text-sm" data-testid="agent-outdated-list">
          {d.outdated.map((r) => (
            <li key={r.machine_id} className="flex items-center gap-3 py-2" data-testid={`agent-outdated-${r.machine_id}`}>
              <span className="font-medium mr-auto">{r.name}</span>
              <span className="text-xs text-slate-500">{r.os} · version {r.version}</span>
              <span className={`text-xs ${r.online ? "text-emerald-700" : "text-slate-400"}`}>{r.online ? "En ligne" : "Hors ligne"}</span>
              {r.update_sent_at && <span className="text-xs text-blue-700">envoyée le {fmtDate(r.update_sent_at)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
